import React, { useCallback, useState } from 'react';
import { CheckboxButton } from '../../../../../components/CheckboxButton';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import { JobService } from '../../../../../services/applicant_tracking/JobService';
import { SendEmailModal } from '../../../../SendEmailModal';
import { SendEmailDto } from '../../../../../services/dtos/EmailDto';
import { User } from '../../../../../entities/User';

interface PropTypes {
  jobId: number;
  jobName: string;
  archiveAction: 'Archive' | 'Unarchive';
  isModalOpen: boolean;
  redirectUri: string;
  currentUser?: User;
  setIsModalOpen: (b: boolean) => void;
}

function redirectTo(path: string, redirectUri?: string) {
  const baseUrl = `/applicant_tracking/jobs/${path}/?`;
  const redirectUrlParam = redirectUri && `redirect_uri=${redirectUri}&`;

  window.location.href = baseUrl + redirectUrlParam;
}

export function ArchiveJobConfirmationModal(props: PropTypes) {
  const [rejectActiveApplications, setRejectActiveApplications] =
    useState(true);
  const [sendRejectionEmails, setSendRejectionEmails] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const emailOptionAvailable = props.currentUser != null;

  const archiveJob = useCallback(
    async (email?: SendEmailDto) => {
      try {
        await JobService.archive(props.jobId, {
          rejectActiveApplications,
          email,
        });
        redirectTo('archive_success', props.redirectUri);
      } catch (e) {
        redirectTo('archive_error', props.redirectUri);
      }
    },
    [props.jobId, props.redirectUri, rejectActiveApplications],
  );

  const onArchivingJob = useCallback(async () => {
    if (
      rejectActiveApplications &&
      sendRejectionEmails &&
      emailOptionAvailable
    ) {
      props.setIsModalOpen(false);
      setIsEmailModalOpen(true);
      return;
    }

    await archiveJob();
    props.setIsModalOpen(false);
  }, [
    rejectActiveApplications,
    sendRejectionEmails,
    emailOptionAvailable,
    archiveJob,
  ]);

  const onSendRejectionEmail = useCallback(
    async (email: SendEmailDto) => {
      await archiveJob(email);
      setIsEmailModalOpen(false);
    },
    [archiveJob],
  );

  const onUnarchivingJob = useCallback(async () => {
    try {
      await JobService.unarchive(props.jobId);
      redirectTo('unarchive_success', props.redirectUri);
    } catch (e) {
      redirectTo('unarchive_error', props.redirectUri);
    }
    props.setIsModalOpen(false);
  }, [props.jobId]);

  return (
    <>
      <ConfirmationModal
        title={`${props.archiveAction} Job`}
        body={
          <>
            <p>
              Are you sure you want to {props.archiveAction.toLowerCase()} the{' '}
              {props.jobName} job?
            </p>
            {props.archiveAction === 'Archive' && (
              <>
                <CheckboxButton
                  name='rejectActiveApplications'
                  label='Reject all active candidates'
                  labelClasses='mb-2'
                  checked={rejectActiveApplications}
                  onChange={() =>
                    setRejectActiveApplications(!rejectActiveApplications)
                  }
                />
                {emailOptionAvailable && (
                  <CheckboxButton
                    name='sendRejectionEmails'
                    label='Send rejection emails to candidates'
                    checked={rejectActiveApplications && sendRejectionEmails}
                    disabled={!rejectActiveApplications}
                    onChange={() =>
                      setSendRejectionEmails(!sendRejectionEmails)
                    }
                  />
                )}
              </>
            )}
          </>
        }
        isOpen={props.isModalOpen}
        onConfirm={
          props.archiveAction === 'Archive' ? onArchivingJob : onUnarchivingJob
        }
        onCancel={() => props.setIsModalOpen(false)}
        size={'md'}
        confirmText={props.archiveAction}
        disableAfterConfirm={false}
      />
      {isEmailModalOpen && props.currentUser != null && (
        <SendEmailModal
          applicationIds={[]}
          hasEmailConnected={props.currentUser.hasEmailConnected}
          isOpen={isEmailModalOpen}
          modalTitle='Send rejection email'
          toEmail='All active candidates'
          warningDescription='This email will be sent to all active candidates being rejected when the job is archived.'
          onCloseModal={() => setIsEmailModalOpen(false)}
          onSendEmail={onSendRejectionEmail}
        />
      )}
    </>
  );
}
